import { err, ok } from 'neverthrow';
import { z } from 'zod/v3';
import {
  BaseAction,
  type ActionResult,
  type ApplicableContext,
  type ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import {
  refreshWorkflowRunViews,
  reportWorkflowRunBatch,
  runNoun,
  workflowRunTargetsFromContext,
  type WorkflowRunActionErrors,
  type WorkflowRunActionsClient,
} from './workflow-run-actions.shared';

/**
 * Re-runs each targeted terminal run's original input through the workflow as
 * it is now, as a NEW run; the runs being retried are left untouched.
 *
 * Retrying exactly one run returns the new run's id as `resourceId` so the
 * caller can follow it; the route page navigates while the workflow editor
 * pushes onto its artifact-panel stack, which is why this action does not
 * navigate itself.
 */
export class RetryWorkflowRunsAction extends BaseAction<
  Record<string, never>,
  WorkflowRunActionErrors
> {
  title = 'Retry runs';
  group = 'Workflow';
  icon = 'refresh-cw';
  inputSchema = z.object({});

  constructor(private readonly client: WorkflowRunActionsClient) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    const targets = workflowRunTargetsFromContext(context);
    return targets !== null && targets.retryableRuns.length > 0;
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<WorkflowRunActionErrors>> {
    const targets = workflowRunTargetsFromContext(context);
    if (targets === null) {
      return ok({});
    }
    const runs = targets.retryableRuns;
    if (runs.length === 0) {
      return ok({});
    }

    const pending = toast.loading(`Retrying ${runNoun(runs.length)}...`);
    const results = await Promise.allSettled(
      runs.map((run) =>
        this.client.resource.workflow.retryRun({
          workflowId: targets.workflowId,
          runId: run.id,
        })
      )
    );
    const createdRunIds = results.flatMap((result) =>
      result.status === 'fulfilled' && result.value.isOk()
        ? [result.value.value.runId]
        : []
    );

    reportWorkflowRunBatch({
      pending,
      total: runs.length,
      succeeded: createdRunIds.length,
      allSucceededMessage: (noun) => `Started ${noun}`,
      noneSucceededMessage: (noun) => `Could not retry ${noun}`,
      partialMessage: (count, noun) => `Retried ${count} of ${noun}`,
    });

    refreshWorkflowRunViews(context);

    if (createdRunIds.length === 0) {
      return err([{ t: 'error', message: 'Failed to retry runs' }]);
    }
    // Only a single-run retry has one obvious run to follow; a bulk retry just
    // refreshes the list.
    const createdRunId = createdRunIds[0];
    if (runs.length === 1 && createdRunId !== undefined) {
      return ok({ resourceId: createdRunId });
    }
    return ok({});
  }
}
